import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { LightboxWrapper } from './styled'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import CloseButton from '../CloseButton'

interface LightboxProps {
  isOpen: boolean
  onClose: () => void
  currentImage: string
  images: string[]
  currentIndex: number
  onPrevious: () => void
  onNext: () => void
  onSelectImage: (index: number) => void
  inventoryNumber?: string
  altText?: string
}

const Lightbox = ({
  isOpen,
  onClose,
  currentImage,
  images,
  currentIndex,
  onPrevious,
  onNext,
  onSelectImage,
  inventoryNumber,
  altText = 'Image',
}: LightboxProps) => {
  const { t } = useTranslation()
  const [isClosing, setIsClosing] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      setIsClosing(false)
    } else if (shouldRender) {
      setIsClosing(true)
      const timer = setTimeout(() => {
        setShouldRender(false)
        setIsClosing(false)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  const handleClose = () => {
    onClose()
  }

  // ESC key to close lightbox
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose()
      }
      if (event.key === 'ArrowLeft' && isOpen) {
        onPrevious()
      }
      if (event.key === 'ArrowRight' && isOpen) {
        onNext()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress)
      document.body.style.overflow = 'hidden' // Prevent background scrolling
      return () => {
        document.removeEventListener('keydown', handleKeyPress)
        document.body.style.overflow = 'unset'
      }
    }
  }, [isOpen, onClose, onPrevious, onNext])

  if (!shouldRender) return null

  return createPortal(
    <LightboxWrapper onClick={handleClose} $isClosing={isClosing}>
      <div className="lightbox-content" onClick={e => e.stopPropagation()}>
        {images.length > 1 && (
          <>
            <button className="nav-button prev" onClick={onPrevious}>
              <SVG src="/icons/lb_arrow_left.svg" />
            </button>
            <button className="nav-button next" onClick={onNext}>
              <SVG src="/icons/lb_arrow_right.svg" />
            </button>
          </>
        )}

        <div className="lightbox-main">
          <img src={currentImage.replace('small_', '')} alt={altText} />

          <div className="lightbox-info">
            {inventoryNumber && (
              <p className="lightbox-inventory">
                {t('inventoryNumber')}: {inventoryNumber}
              </p>
            )}

            {images.length > 1 && (
              <div className="lightbox-thumbnails">
                {images.map((image, index) => (
                  <img
                    key={index}
                    className={`lightbox-thumbnail ${index === currentIndex ? 'selected' : ''}`}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    onClick={() => onSelectImage(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <CloseButton onClick={onClose} className="close-button" />
      </div>
    </LightboxWrapper>,
    document.body,
  )
}

export default Lightbox
